import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {selectEditingMobile} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {ColorPicker} from '~/common/fabric/CreateDesign/components/CardEditor/actions/ColorPicker/ColorPicker';
import {FontsMobile} from '~/common/fabric/CreateDesign/components/CardEditor/actions/Dropdown/FontsMobile';
import {TextFormat} from '~/common/fabric/CreateDesign/components/CardEditor/actions/TextFormat/TextFormat';
import {TextSize} from '~/common/fabric/CreateDesign/components/CardEditor/actions/TextSize/TextSize';
import {ContentBase} from '~/common/fabric/CreateDesign/components/CardEditor/Mobile/Contents/ContentBase';
import {SpacingHeight} from '~/common/fabric/CreateDesign/components/CardEditor/Mobile/Contents/SpacingHeight/SpacingHeight';
import {MobileButton} from '~/common/fabric/CreateDesign/components/CardEditor/Mobile/MobileButton';
import {UyoEditor} from '~/common/fabric/CreateDesign/components/CardEditor/Mobile/UyoEditor/UyoEditor';
import {menuMappingKeys} from '~/common/fabric/CreateDesign/constants';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {FontIcon} from '~library/svg/FontIcon';
import {FontSizeIcon} from '~library/svg/FontSizeIcon';
import {KeyboardIcon} from '~library/svg/KeyboardIcon';
import {LineHeightIcon} from '~library/svg/LineHeightIcon';
import {TextColorIcon} from '~library/svg/TextColorIcon';
import {TextFormatIcon} from '~library/svg/TextFormatIcon';
import './CardEditorMobile.sass';

export const CardEditorMobile = () => {
  const {canvas, activeObjects} = useContext(FabricContext);
  const [showMenu, setShowMenu] = useState(false);
  const [activeMenu, setActiveMenu] = useState();
  const editingMobile = useSelector(selectEditingMobile);
  const dispatch = useDispatch();

  const onClickMenu = useCallback(
    (selectedMenu) => () => {
      setShowMenu(true);
      setActiveMenu(selectedMenu);
    },
    []
  );

  useEffect(() => {
    if (!editingMobile) {
      setShowMenu(editingMobile);
    }
  }, [editingMobile]);

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      const canvasWrapper = document.querySelector('.event-create-design__content');
      canvasWrapper.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 1);
  }, []);

  const onKeyboardButtonClick = () =>
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {textAreaOpen: true},
    });

  const onClickCloseHandler = useCallback(() => {
    scrollToTop();
    setShowMenu(false);
    canvas.renderAll();
  }, [canvas, scrollToTop]);

  const onCloseEditorClick = useCallback(() => {
    setActiveMenu(null);
    setShowMenu(false);
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
    canvas?.discardActiveObject()?.renderAll();
  }, [canvas, dispatch]);

  useEffect(() => {
    if (showMenu && activeMenu === menuMappingKeys.FONT_STYLE && !!activeObjects?.length) {
      const canvasWrapper = document.querySelector('.event-create-design__content');
      const y = activeObjects[0].top / 2 - canvasWrapper.clientHeight / 2 - 120;

      canvasWrapper.scrollTo({
        top: y,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [activeMenu, activeObjects, showMenu]);

  if (!editingMobile) {
    return null;
  }

  return (
    <div className="card_editor--mobile">
      <div className="card_editor--mobile__close_btn_container__title">
        <Typography
          variant="label2"
          className="card_editor--mobile__close_btn_container__title__text"
        >
          Text
        </Typography>
        <Button
          variant="unstyled-button"
          className="card_editor--mobile__close_btn"
          onClick={onCloseEditorClick}
          data-qa-id="mobile-close-btn"
        >
          <CloseLineIcon ratio={0.75} />
        </Button>
      </div>
      <div
        className={`card_editor--mobile__content ${
          showMenu ? 'card_editor--mobile__content--active' : ''
        }`}
      >
        <ContentBase
          resetDataQaId="step1-textstyle-reset"
          closeDataQaId="step1-textstyle-close"
          title="Font Style"
          visible={activeMenu === menuMappingKeys.FONT_STYLE}
          style={menuMappingKeys.FONT_STYLE}
          onCloseClick={onClickCloseHandler}
        >
          <FontsMobile />
        </ContentBase>

        <ContentBase
          resetDataQaId="step1-textsize-reset"
          closeDataQaId="step1-textsize-close"
          title="Font Size"
          onCloseClick={onClickCloseHandler}
          visible={activeMenu === menuMappingKeys.FONT_SIZE}
          style={menuMappingKeys.FONT_SIZE}
        >
          <TextSize />
        </ContentBase>

        <ContentBase
          resetDataQaId="step1-textformat-reset"
          closeDataQaId="step1-textformat-close"
          title="Text Format"
          onCloseClick={onClickCloseHandler}
          visible={activeMenu === menuMappingKeys.TEXT_FORMAT}
          style={menuMappingKeys.TEXT_FORMAT}
        >
          <TextFormat />
        </ContentBase>

        <ContentBase
          resetDataQaId="step1-textspacing-reset"
          closeDataQaId="step1-textspacing-close"
          title="Spacing"
          onCloseClick={onClickCloseHandler}
          visible={activeMenu === menuMappingKeys.SPACING}
          style={menuMappingKeys.SPACING}
        >
          <SpacingHeight />
        </ContentBase>

        <ContentBase
          resetDataQaId="step1-textcolor-reset"
          closeDataQaId="step1-textcolor-close"
          title="Color"
          onCloseClick={onClickCloseHandler}
          visible={activeMenu === menuMappingKeys.COLOR}
          style={menuMappingKeys.COLOR}
          bodyStyle={{
            paddingRight: 0,
          }}
        >
          <ColorPicker />
        </ContentBase>
        <UyoEditor
          activeMenu={activeMenu}
          scrollToTop={scrollToTop}
          setActiveMenu={setActiveMenu}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
        />
      </div>
      <div className="card_editor--mobile__buttons">
        <MobileButton
          icon={<KeyboardIcon />}
          onClick={onKeyboardButtonClick}
          active={activeMenu === menuMappingKeys.KEYBOARD}
          title="Edit Text"
          dataQaId="step1-texteditor"
        />
        <MobileButton
          icon={<FontIcon />}
          onClick={onClickMenu(menuMappingKeys.FONT_STYLE)}
          title="Font"
          dataQaId="step1-textstyle"
        />
        <MobileButton
          icon={<FontSizeIcon />}
          onClick={onClickMenu(menuMappingKeys.FONT_SIZE)}
          title="Size"
          dataQaId="step1-textsize"
        />
        <MobileButton
          icon={<TextColorIcon color={activeObjects?.[0]?.fill} />}
          onClick={onClickMenu(menuMappingKeys.COLOR)}
          title="Color"
          dataQaId="step1-textcolor"
        />
        <MobileButton
          icon={<TextFormatIcon />}
          onClick={onClickMenu(menuMappingKeys.TEXT_FORMAT)}
          title="Format"
          dataQaId="step1-textformat"
        />
        <MobileButton
          icon={<LineHeightIcon />}
          onClick={onClickMenu(menuMappingKeys.SPACING)}
          title="Spacing"
          dataQaId="step1-textspacing"
        />
      </div>
    </div>
  );
};
